import { useState, useRef } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, FileText, FileSpreadsheet, ChevronLeft, ChevronRight, Send, FileBarChart2, RotateCcw, CheckCircle2, Paperclip, X, Download } from "lucide-react";
import { Badge } from "../components/ui/badge";

const MODOS_PAGAMENTO = [
  "01-Dinheiro", "02-Cheque", "03-Cartão de Crédito", "04-Cartão de Débito",
  "05-Crédito Loja", "10-Vale Alimentação", "11-Vale Refeição", "12-Vale Presente",
  "13-Vale Combustível", "15-Boleto Bancário", "16-Depósito Bancário", "17-PIX",
  "18-Transferência Bancária / Carteira Digital", "19-Programa de Fidelidade / Cashback",
  "90-Sem Pagamento", "99-Outros",
];

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pendente: { label: "Pendente", color: "bg-yellow-100 text-yellow-800" },
  pago: { label: "Pago", color: "bg-green-100 text-green-800" },
  excluido: { label: "Excluído", color: "bg-red-100 text-red-800" },
};

const emptyForm = {
  emitenteId: 0,
  destinatarioId: 0,
  numeroParcela: 1,
  totalParcelas: 1,
  descricao: "",
  tipoPagamento: "",
  valorParcela: 0,
  dataVencimento: "",
  observacao: "",
};

const emptyEdit = {
  id: 0,
  descricao: "",
  tipoPagamento: "",
  valorParcela: 0,
  dataVencimento: "",
  status: "pendente",
  dataRecebimento: "",
  valorRecebido: 0,
  observacao: "",
};

export default function SomenteConsultaContasReceber() {
  const { user } = useGoogleAuth();
  const emitenteId = user?.emitenteId;

  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    dataVencimentoDe: "",
    dataVencimentoAte: "",
  });
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [editForm, setEditForm] = useState({ ...emptyEdit });

  // Estados para geração de boleto
  const [showBoletoModal, setShowBoletoModal] = useState(false);
  const [boletoParcelaId, setBoletoParcelaId] = useState<number | null>(null);
  const [boletoContaBancariaId, setBoletoContaBancariaId] = useState<string>("");
  const [boletoGerado, setBoletoGerado] = useState<any>(null);

  // Estados para baixa e arquivos
  const [showBaixaModal, setShowBaixaModal] = useState(false);
  const [baixaParcelaId, setBaixaParcelaId] = useState<number | null>(null);
  const [showArquivosModal, setShowArquivosModal] = useState(false);
  const [arquivosParcelaId, setArquivosParcelaId] = useState<number | null>(null);

  const queryParams = {
    emitenteId: emitenteId ?? undefined,
    status: filters.status || undefined,
    dataVencimentoDe: filters.dataVencimentoDe || undefined,
    dataVencimentoAte: filters.dataVencimentoAte || undefined,
    page,
    pageSize: 20,
  };

  const { data, refetch, isLoading } = trpc.accountsReceivable.list.useQuery(queryParams);
  const { data: emitentes } = trpc.emitentes.list.useQuery();
  const { data: destinatariosData } = trpc.destinatarios.list.useQuery({ emitenteId: emitenteId ?? undefined });
  const { data: contasBancarias } = trpc.bankAccounts.list.useQuery({ emitenteId: emitenteId ?? undefined });

  // Busca boleto existente para a parcela selecionada
  const { data: boletoExistente, refetch: refetchBoleto } = trpc.boletos.getByParcela.useQuery(
    { parcelaId: boletoParcelaId! },
    { enabled: !!boletoParcelaId }
  );

  const createMutation = trpc.accountsReceivable.create.useMutation({
    onSuccess: () => { toast.success("Parcela criada com sucesso!"); setShowCreate(false); setForm({ ...emptyForm }); refetch(); },
    onError: (e) => toast.error(e.message),
  });

  const updateMutation = trpc.accountsReceivable.update.useMutation({
    onSuccess: () => { toast.success("Parcela atualizada!"); setShowEdit(false); refetch(); },
    onError: (e) => toast.error(e.message),
  });

  const enviarCobrancaMutation = trpc.accountsReceivable.enviarCobranca.useMutation({
    onSuccess: (data) => {
      if (data.aviso) {
        toast.info(data.aviso);
      } else {
        toast.success("Cobrança enviada com sucesso!");
      }
    },
    onError: (err) => toast.error(err.message || "Erro ao enviar cobrança"),
  });

  const excluirMutation = trpc.accountsReceivable.excluir.useMutation({
    onSuccess: () => { toast.success("Parcela excluída!"); refetch(); },
    onError: (e) => toast.error(e.message),
  });

  const gerarBoletoMutation = trpc.boletos.gerarParaParcela.useMutation({
    onSuccess: (data) => {
      toast.success("Boleto gerado com sucesso!");
      setBoletoGerado(data);
      refetchBoleto();
    },
    onError: (e) => toast.error(e.message || "Erro ao gerar boleto"),
  });

  const reemitirBoletoMutation = trpc.boletos.reemitir.useMutation({
    onSuccess: (data) => {
      toast.success("Boleto reemitido com sucesso!");
      setBoletoGerado(data);
      refetchBoleto();
    },
    onError: (e) => toast.error(e.message || "Erro ao reemitir boleto"),
  });

  const allRows = data?.rows ?? [];
  const rows = searchTerm
    ? allRows.filter((r: any) => {
        const s = searchTerm.toLowerCase();
        return (
          r.destinatarioNome?.toLowerCase().includes(s) ||
          r.descricao?.toLowerCase().includes(s) ||
          r.tipoPagamento?.toLowerCase().includes(s) ||
          r.status?.toLowerCase().includes(s) ||
          r.numeroNfe?.toString().includes(s) ||
          r.id?.toString().includes(s)
        );
      })
    : allRows;
  const totalPages = data?.totalPages ?? 1;
  const total = data?.total ?? 0;

  // Contas bancárias ativas para boleto
  const contasBancariasAtivas = (contasBancarias ?? []).filter((c: any) =>
    c.ativo && ['341', '237', '033'].includes(c.codigoBanco)
  );

  function handleEdit(row: any) {
    setEditForm({
      id: row.id,
      descricao: row.descricao ?? "",
      tipoPagamento: row.tipoPagamento ?? "",
      valorParcela: parseFloat(row.valorParcela),
      dataVencimento: row.dataVencimento?.split("T")[0] ?? "",
      status: row.status,
      dataRecebimento: row.dataRecebimento?.split("T")[0] ?? "",
      valorRecebido: row.valorRecebido ? parseFloat(row.valorRecebido) : 0,
      observacao: row.observacao ?? "",
    });
    setShowEdit(true);
  }

  function handleAbrirBoleto(row: any) {
    setBoletoParcelaId(row.id);
    setBoletoContaBancariaId("");
    setBoletoGerado(null);
    setShowBoletoModal(true);
  }

  function handleExportPDF() {
    const rows_str = rows.map((r: any) =>
      `${r.id}\t${r.destinatarioNome ?? ""}\t${r.descricao ?? ""}\t${r.tipoPagamento ?? ""}\t${formatCurrency(r.valorParcela)}\t${formatDate(r.dataVencimento)}\t${r.status}`
    ).join("\n");
    const content = `CONTAS A RECEBER\n\nID\tDestinatário\tDescrição\tPagamento\tValor\tVencimento\tStatus\n${rows_str}`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "contas_receber.txt"; a.click();
    URL.revokeObjectURL(url);
    toast.info("Exportação gerada (formato texto). Para PDF completo, use a impressão do navegador.");
  }

  function handleExportExcel() {
    const header = "ID\tDestinatário\tCNPJ\tDescrição\tTipo Pagamento\tValor\tVencimento\tStatus\tData Recebimento\tValor Recebido\tNF-e\n";
    const rows_str = rows.map((r: any) =>
      `${r.id}\t${r.destinatarioNome ?? ""}\t${r.destinatarioCnpj ?? ""}\t${r.descricao ?? ""}\t${r.tipoPagamento ?? ""}\t${r.valorParcela}\t${formatDate(r.dataVencimento)}\t${r.status}\t${r.dataRecebimento ? formatDate(r.dataRecebimento) : ""}\t${r.valorRecebido ?? ""}\t${r.numeroNfe ?? ""}`
    ).join("\n");
    const content = header + rows_str;
    const blob = new Blob(["\uFEFF" + content], { type: "text/tab-separated-values;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "contas_receber.xls"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Arquivo Excel exportado!");
  }

  function formatCurrency(v: any) {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(parseFloat(v) || 0);
  }

  function formatDate(d: any) {
    if (!d) return "";
    return new Date(d.toString().includes("T") ? d : d + "T12:00:00").toLocaleDateString("pt-BR");
  }

  function isBoleto(tipoPagamento: string | null | undefined) {
    if (!tipoPagamento) return false;
    return tipoPagamento.toLowerCase().includes("boleto") || tipoPagamento === "15-Boleto Bancário";
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Consulta de Contas a Receber</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExportPDF}>
            <FileText className="w-4 h-4 mr-1" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportExcel}>
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4">
          <div className="mb-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Filtros:</span>
              <Input
                placeholder="Buscar por qualquer coisa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-md"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label>Status</Label>
              <Select value={filters.status || "todos"} onValueChange={(v) => { setFilters(f => ({ ...f, status: v === "todos" ? "" : v })); setPage(1); }}>
                <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="pago">Pago</SelectItem>
                  <SelectItem value="excluido">Excluído</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vencimento de</Label>
              <Input type="date" value={filters.dataVencimentoDe} onChange={e => { setFilters(f => ({ ...f, dataVencimentoDe: e.target.value })); setPage(1); }} />
            </div>
            <div>
              <Label>Vencimento até</Label>
              <Input type="date" value={filters.dataVencimentoAte} onChange={e => { setFilters(f => ({ ...f, dataVencimentoAte: e.target.value })); setPage(1); }} />
            </div>
            <div className="flex items-end">
              <Button variant="outline" size="sm" onClick={() => { setFilters({ status: "", dataVencimentoDe: "", dataVencimentoAte: "" }); setSearchTerm(""); setPage(1); }}>
                <Search className="w-4 h-4 mr-1" /> Limpar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {total} registro{total !== 1 ? "s" : ""} encontrado{total !== 1 ? "s" : ""}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-center text-gray-500 py-8">Carregando...</p>
          ) : rows.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Nenhuma parcela encontrada.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-600">
                    <th className="pb-2 pr-3">ID</th>
                    <th className="pb-2 pr-3">Emitente</th>
                    <th className="pb-2 pr-3">Destinatário</th>
                    <th className="pb-2 pr-3">Descrição</th>
                    <th className="pb-2 pr-3">Pagamento</th>
                    <th className="pb-2 pr-3 text-right">Valor</th>
                    <th className="pb-2 pr-3">Vencimento</th>
                    <th className="pb-2 pr-3">NF-e</th>
                    <th className="pb-2 pr-3">Status</th>
                    <th className="pb-2 pr-3">Arquivos</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row: any) => {
                    const st = STATUS_LABELS[row.status] ?? { label: row.status, color: "bg-gray-100 text-gray-800" };
                    return (
                      <tr key={row.id} className="border-b hover:bg-gray-50">
                        <td className="py-2 pr-3 text-gray-500">{row.id}</td>
                        <td className="py-2 pr-3">{row.emitenteNome ?? "-"}</td>
                        <td className="py-2 pr-3 font-medium">{row.destinatarioNome ?? "-"}</td>
                        <td className="py-2 pr-3">{row.descricao ?? "-"}</td>
                        <td className="py-2 pr-3">{row.tipoPagamento ?? "-"}</td>
                        <td className="py-2 pr-3 text-right font-medium">{formatCurrency(row.valorParcela)}</td>
                        <td className="py-2 pr-3">{formatDate(row.dataVencimento)}</td>
                        <td className="py-2 pr-3">{row.numeroNfe ?? "-"}</td>
                        <td className="py-2 pr-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}>{st.label}</span>
                        </td>
                        <td className="py-2 pr-3">
                          <button
                            className="text-gray-400 hover:text-blue-600"
                            title="Arquivos e comprovantes"
                            onClick={() => { setArquivosParcelaId(row.id); setShowArquivosModal(true); }}
                          >
                            <Paperclip className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-500">Página {page} de {totalPages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Criar */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nova Parcela Manual</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {!emitenteId && (
              <div>
                <Label>Emitente *</Label>
                <Select value={form.emitenteId ? String(form.emitenteId) : ""} onValueChange={v => setForm(f => ({ ...f, emitenteId: parseInt(v) }))}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {(emitentes ?? []).map((e: any) => <SelectItem key={e.id} value={String(e.id)}>{e.razaoSocial}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Destinatário *</Label>
              <Select value={form.destinatarioId ? String(form.destinatarioId) : ""} onValueChange={v => setForm(f => ({ ...f, destinatarioId: parseInt(v) }))}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {((destinatariosData as any)?.data ?? []).map((d: any) => (
                    <SelectItem key={d.id} value={String(d.id)}>{d.razaoSocial}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Parcela Nº *</Label>
                <Input type="number" min={1} value={form.numeroParcela} onChange={e => setForm(f => ({ ...f, numeroParcela: parseInt(e.target.value) || 1 }))} />
              </div>
              <div>
                <Label>Total de Parcelas *</Label>
                <Input type="number" min={1} value={form.totalParcelas} onChange={e => setForm(f => ({ ...f, totalParcelas: parseInt(e.target.value) || 1 }))} />
              </div>
            </div>
            <div>
              <Label>Descrição</Label>
              <Input value={form.descricao} onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))} placeholder="Ex: Parcela 1/3 - Pedido 42" />
            </div>
            <div>
              <Label>Tipo de Pagamento</Label>
              <Select value={form.tipoPagamento || "none"} onValueChange={v => setForm(f => ({ ...f, tipoPagamento: v === "none" ? "" : v }))}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Não informado</SelectItem>
                  {MODOS_PAGAMENTO.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Valor *</Label>
                <Input type="number" step="0.01" min={0} value={form.valorParcela} onChange={e => setForm(f => ({ ...f, valorParcela: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div>
                <Label>Vencimento *</Label>
                <Input type="date" value={form.dataVencimento} onChange={e => setForm(f => ({ ...f, dataVencimento: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Observação</Label>
              <Input value={form.observacao} onChange={e => setForm(f => ({ ...f, observacao: e.target.value }))} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
              <Button onClick={() => createMutation.mutate({
                emitenteId: emitenteId ?? form.emitenteId,
                destinatarioId: form.destinatarioId,
                numeroParcela: form.numeroParcela,
                totalParcelas: form.totalParcelas,
                descricao: form.descricao || null,
                tipoPagamento: form.tipoPagamento || null,
                valorParcela: form.valorParcela,
                dataVencimento: form.dataVencimento,
                observacao: form.observacao || null,
              })} disabled={createMutation.isPending}>
                {createMutation.isPending ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Editar */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Editar Parcela #{editForm.id}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Descrição</Label>
              <Input value={editForm.descricao} onChange={e => setEditForm(f => ({ ...f, descricao: e.target.value }))} />
            </div>
            <div>
              <Label>Tipo de Pagamento</Label>
              <Select value={editForm.tipoPagamento || "none"} onValueChange={v => setEditForm(f => ({ ...f, tipoPagamento: v === "none" ? "" : v }))}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Não informado</SelectItem>
                  {MODOS_PAGAMENTO.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Valor</Label>
                <Input type="number" step="0.01" value={editForm.valorParcela} onChange={e => setEditForm(f => ({ ...f, valorParcela: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div>
                <Label>Vencimento</Label>
                <Input type="date" value={editForm.dataVencimento} onChange={e => setEditForm(f => ({ ...f, dataVencimento: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={editForm.status} onValueChange={v => setEditForm(f => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="pago">Pago</SelectItem>
                  <SelectItem value="excluido">Excluído</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editForm.status === "pago" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Data Recebimento</Label>
                  <Input type="date" value={editForm.dataRecebimento} onChange={e => setEditForm(f => ({ ...f, dataRecebimento: e.target.value }))} />
                </div>
                <div>
                  <Label>Valor Recebido</Label>
                  <Input type="number" step="0.01" value={editForm.valorRecebido} onChange={e => setEditForm(f => ({ ...f, valorRecebido: parseFloat(e.target.value) || 0 }))} />
                </div>
              </div>
            )}
            <div>
              <Label>Observação</Label>
              <Input value={editForm.observacao} onChange={e => setEditForm(f => ({ ...f, observacao: e.target.value }))} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowEdit(false)}>Cancelar</Button>
              <Button onClick={() => updateMutation.mutate({
                id: editForm.id,
                descricao: editForm.descricao || null,
                tipoPagamento: editForm.tipoPagamento || null,
                valorParcela: editForm.valorParcela,
                dataVencimento: editForm.dataVencimento,
                status: editForm.status,
                dataRecebimento: editForm.dataRecebimento || null,
                valorRecebido: editForm.valorRecebido || null,
                observacao: editForm.observacao || null,
              })} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Boleto */}
      <Dialog open={showBoletoModal} onOpenChange={(open) => {
        setShowBoletoModal(open);
        if (!open) { setBoletoParcelaId(null); setBoletoGerado(null); setBoletoContaBancariaId(""); }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Boleto Bancário — Parcela #{boletoParcelaId}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Boleto existente */}
            {boletoExistente && !boletoGerado && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
                <p className="text-sm font-semibold text-green-800">Boleto já gerado</p>
                <div className="text-xs text-gray-700 space-y-1">
                  <p><span className="font-medium">Nosso Número:</span> {boletoExistente.nossoNumero}</p>
                  <p><span className="font-medium">Linha Digitável:</span> <span className="font-mono">{boletoExistente.linhaDigitavel}</span></p>
                  <p><span className="font-medium">Status:</span> {boletoExistente.status}</p>
                  <p><span className="font-medium">Banco:</span> {boletoExistente.nomeBanco} ({boletoExistente.codigoBanco})</p>
                </div>
                {boletoExistente.pdfPath && (
                  <a
                    href={boletoExistente.pdfPath}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                  >
                    <FileText className="w-4 h-4" /> Visualizar PDF do Boleto
                  </a>
                )}
                <div className="pt-2">
                  <p className="text-xs text-gray-500 mb-2">Para reemitir, selecione a conta bancária e clique em Reemitir:</p>
                  <Select value={boletoContaBancariaId} onValueChange={setBoletoContaBancariaId}>
                    <SelectTrigger><SelectValue placeholder="Selecione a conta bancária" /></SelectTrigger>
                    <SelectContent>
                      {contasBancariasAtivas.map((c: any) => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {c.nomeBanco} ({c.codigoBanco}) — Ag. {c.agencia} / Cta. {c.conta}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    className="mt-2 w-full"
                    variant="outline"
                    disabled={!boletoContaBancariaId || reemitirBoletoMutation.isPending}
                    onClick={() => {
                      if (confirm("Isso cancelará o boleto anterior e gerará um novo. Confirmar?")) {
                        reemitirBoletoMutation.mutate({ boletoId: boletoExistente.id });
                      }
                    }}
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    {reemitirBoletoMutation.isPending ? "Reemitindo..." : "Reemitir Boleto"}
                  </Button>
                </div>
              </div>
            )}

            {/* Boleto recém gerado */}
            {boletoGerado && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                <p className="text-sm font-semibold text-blue-800">Boleto gerado com sucesso!</p>
                <div className="text-xs text-gray-700 space-y-1">
                  <p><span className="font-medium">Nosso Número:</span> {boletoGerado.nossoNumero}</p>
                  <p><span className="font-medium">Linha Digitável:</span> <span className="font-mono break-all">{boletoGerado.linhaDigitavel}</span></p>
                  <p><span className="font-medium">Código de Barras:</span> <span className="font-mono break-all text-xs">{boletoGerado.codigoBarras}</span></p>
                </div>
                {boletoGerado.pdfPath && (
                  <a
                    href={boletoGerado.pdfPath}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                  >
                    <FileText className="w-4 h-4" /> Visualizar PDF do Boleto
                  </a>
                )}
              </div>
            )}

            {/* Gerar novo boleto (quando não há boleto existente) */}
            {!boletoExistente && !boletoGerado && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">Nenhum boleto gerado para esta parcela. Selecione a conta bancária para gerar:</p>
                {contasBancariasAtivas.length === 0 ? (
                  <p className="text-sm text-red-500">Nenhuma conta bancária configurada para boleto (Itaú 341, Bradesco 237 ou Santander 033).</p>
                ) : (
                  <>
                    <Select value={boletoContaBancariaId} onValueChange={setBoletoContaBancariaId}>
                      <SelectTrigger><SelectValue placeholder="Selecione a conta bancária" /></SelectTrigger>
                      <SelectContent>
                        {contasBancariasAtivas.map((c: any) => (
                          <SelectItem key={c.id} value={String(c.id)}>
                            {c.nomeBanco} ({c.codigoBanco}) — Ag. {c.agencia} / Cta. {c.conta}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      className="w-full"
                      disabled={!boletoContaBancariaId || gerarBoletoMutation.isPending}
                      onClick={() => {
                        if (boletoParcelaId && boletoContaBancariaId) {
                          gerarBoletoMutation.mutate({
                            parcelaId: boletoParcelaId,
                            contaBancariaId: parseInt(boletoContaBancariaId),
                          });
                        }
                      }}
                    >
                      <FileBarChart2 className="w-4 h-4 mr-2" />
                      {gerarBoletoMutation.isPending ? "Gerando..." : "Gerar Boleto"}
                    </Button>
                  </>
                )}
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => setShowBoletoModal(false)}>Fechar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {showBaixaModal && baixaParcelaId && (
        <ModalBaixaReceber id={baixaParcelaId} onClose={() => { setShowBaixaModal(false); setBaixaParcelaId(null); refetch(); }} />
      )}
      {showArquivosModal && arquivosParcelaId && (
        <ModalArquivosReceber id={arquivosParcelaId} onClose={() => { setShowArquivosModal(false); setArquivosParcelaId(null); }} />
      )}
    </div>
  );
}

// ============================================================
// MODAL BAIXA RECEBER
// ============================================================
function ModalBaixaReceber({ id, onClose }: { id: number; onClose: () => void }) {
  const utils = trpc.useUtils();
  const conta = trpc.accountsReceivable.getById.useQuery({ id });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [form, setForm] = useState({
    dataRecebimento: new Date().toISOString().split("T")[0],
    valorRecebido: 0,
    observacao: "",
  });

  const addArquivoMut = trpc.accountsReceivable.arquivos.add.useMutation({
    onError: (e) => toast.error(e.message),
  });

  const baixarMut = trpc.accountsReceivable.baixar.useMutation({
    onSuccess: () => { toast.success("Baixa realizada com sucesso!"); utils.accountsReceivable.invalidate(); onClose(); },
    onError: (e) => toast.error(e.message),
  });

  const [valorPreenchido, setValorPreenchido] = useState(false);
  if (conta.data && !valorPreenchido) {
    setForm(f => ({ ...f, valorRecebido: parseFloat(conta.data.valorParcela) || 0 }));
    setValorPreenchido(true);
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch("/api/upload", { method: "POST", credentials: "include", body: formData });
      if (!res.ok) throw new Error("Falha no upload");
      const data = await res.json();
      const tipo = file.name.endsWith(".xml") ? "xml" : file.name.endsWith(".pdf") ? "pdf" : "comprovante";
      await addArquivoMut.mutateAsync({ contaReceberId: id, nome: file.name, url: data.url, tipo });
      toast.success(`Arquivo "${file.name}" anexado!`);
    } catch (err: any) {
      toast.error(err.message || "Erro ao fazer upload");
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSubmit = () => {
    baixarMut.mutate({
      id,
      dataRecebimento: form.dataRecebimento,
      valorRecebido: Number(form.valorRecebido),
      observacao: form.observacao || null,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-bold">Dar Baixa</h2>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          {conta.data && (
            <div className="bg-gray-50 rounded p-3 text-sm space-y-1">
              {conta.data.pedidoId && <p><strong>Pedido:</strong> #{conta.data.pedidoId}</p>}
              {conta.data.numeroParcela && conta.data.totalParcelas && (
                <p><strong>Parcela:</strong> {conta.data.numeroParcela}/{conta.data.totalParcelas}</p>
              )}
              {conta.data.tipoPagamento && <p><strong>Modo de Pagamento:</strong> {conta.data.tipoPagamento}</p>}
              <p><strong>Valor:</strong> R$ {Number(conta.data.valorParcela).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</p>
              <p><strong>Vencimento:</strong> {conta.data.dataVencimento ? new Date(conta.data.dataVencimento + "T12:00:00").toLocaleDateString("pt-BR") : "-"}</p>
            </div>
          )}
          <div>
            <Label>Data de Recebimento *</Label>
            <Input type="date" value={form.dataRecebimento} onChange={(e) => setForm({ ...form, dataRecebimento: e.target.value })} />
          </div>
          <div>
            <Label>Valor Recebido *</Label>
            <Input type="number" step="0.01" value={form.valorRecebido} onChange={(e) => setForm({ ...form, valorRecebido: parseFloat(e.target.value) || 0 })} />
          </div>
          <div>
            <Label>Observação</Label>
            <textarea className="border rounded px-2 py-1.5 text-sm w-full h-16 resize-none" value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })} />
          </div>
          <div>
            <Label>Comprovante (opcional)</Label>
            <div className="flex items-center gap-2 mt-1">
              <Button type="button" variant="outline" size="sm" disabled={uploadingFile} onClick={() => fileInputRef.current?.click()}>
                {uploadingFile ? "Enviando..." : "Selecionar arquivo"}
              </Button>
              <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} />
            </div>
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2 flex-shrink-0">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={baixarMut.isPending || !form.dataRecebimento}>
            {baixarMut.isPending ? "Salvando..." : "Dar Baixa"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MODAL ARQUIVOS RECEBER
// ============================================================
function ModalArquivosReceber({ id, onClose }: { id: number; onClose: () => void }) {
  const utils = trpc.useUtils();
  const arquivos = trpc.accountsReceivable.arquivos.list.useQuery({ contaReceberId: id });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [visualizando, setVisualizando] = useState<any>(null);

  const addArquivoMut = trpc.accountsReceivable.arquivos.add.useMutation({
    onSuccess: () => { toast.success("Arquivo adicionado!"); utils.accountsReceivable.arquivos.list.invalidate(); },
    onError: (e) => toast.error(e.message),
  });

  const deleteArquivoMut = trpc.accountsReceivable.arquivos.delete.useMutation({
    onSuccess: () => { toast.success("Arquivo removido!"); utils.accountsReceivable.arquivos.list.invalidate(); },
    onError: (e) => toast.error(e.message),
  });

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch("/api/upload", { method: "POST", credentials: "include", body: formData });
      if (!res.ok) throw new Error("Falha no upload");
      const data = await res.json();
      const tipo = file.name.endsWith(".xml") ? "xml" : file.name.endsWith(".pdf") ? "pdf" : "comprovante";
      await addArquivoMut.mutateAsync({ contaReceberId: id, nome: file.name, url: data.url, tipo });
    } catch (err: any) {
      toast.error(err.message || "Erro ao fazer upload");
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const isImage = (url: string) => /\.(png|jpg|jpeg|gif|webp)$/i.test(url);
  const isPdf = (url: string) => /\.pdf$/i.test(url);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-bold">Arquivos — Parcela #{id}</h2>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">{arquivos.data?.length ?? 0} arquivo(s) anexado(s)</p>
          </div>

          {arquivos.data?.map?.((arq: any) => (
            <div key={arq.id} className="border rounded p-2 space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium truncate flex-1">{arq.nome}</span>
                <div className="flex gap-1 flex-shrink-0">
                  <a href={arq.url} target="_blank" rel="noopener noreferrer" title="Baixar" className="text-blue-600 hover:text-blue-800">
                    <Download className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
              <p className="text-xs text-gray-400">{new Date(arq.criadoEm).toLocaleString("pt-BR")}</p>
              {visualizando === arq.id ? (
                <div className="mt-1">
                  {isPdf(arq.url) && (
                    <iframe src={arq.url} className="w-full h-64 border rounded" title={arq.nome} />
                  )}
                  {isImage(arq.url) && (
                    <img src={arq.url} alt={arq.nome} className="max-w-full max-h-64 rounded border" />
                  )}
                  <button className="text-xs text-gray-500 mt-1 hover:underline" onClick={() => setVisualizando(null)}>Fechar visualização</button>
                </div>
              ) : (
                (isPdf(arq.url) || isImage(arq.url)) && (
                  <button className="text-xs text-blue-600 hover:underline" onClick={() => setVisualizando(arq.id)}>Visualizar</button>
                )
              )}
            </div>
          ))}

          {(!arquivos.data?.length) && (
            <p className="text-sm text-gray-400 text-center py-4">Nenhum arquivo anexado</p>
          )}
        </div>
        <div className="p-4 border-t flex justify-end flex-shrink-0">
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </div>
      </div>
    </div>
  );
}
